import clsx, { type ClassValue } from 'clsx'
import React from 'react'

import Link from './Link'

const hoverStyles = {
  darkgreen: 'hover:bg-dark_mint',
  white: 'hover:bg-light_mint2',
  yellow: 'hover:bg-light_yellow',
  dark: 'hover:bg-light_mint2 hover:text-dark'
}

const baseStyles = {
  primary:
    'h-11 lg:h-16 rounded-xl lg:rounded-3xl px-4 py-2.5 text-custom-md lg:text-custom-lg font-medium shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2',
  secondary: 'w-[150px] h-[44px] md:w-[110px] rounded-2xl p-3 text-base leading-none text-dark'
}

const variantStyles = {
  primary: {
    darkgreen: `bg-dark_green text-white ${hoverStyles.darkgreen}`,
    white: `bg-white text-dark_green border-dark_green border-[1px] ${hoverStyles.white}`,
    yellow: `bg-primary_yellow text-dark ${hoverStyles.yellow} disabled:bg-gray`,
    dark: `bg-dark text-white border-[1px] border-white ${hoverStyles.dark}`
  },
  secondary: {
    darkgreen: `bg-dark_green text-white ${hoverStyles.darkgreen}`,
    white: `bg-white text-dark_green border-dark_green border-[1px] ${hoverStyles.white}`,
    yellow: `bg-primary_yellow text-dark ${hoverStyles.yellow}`,
    dark: `bg-dark text-white border-[1px] border-white ${hoverStyles.dark}`
  }
}

type VariantKey = keyof typeof variantStyles
type ColorKey<Variant extends VariantKey> = keyof (typeof variantStyles)[Variant]

type ButtonProps<Variant extends VariantKey, Color extends ColorKey<Variant>> = {
  variant?: Variant
  color?: Color
  target?: string
  rel?: string
} & (
  | Omit<React.ComponentPropsWithoutRef<typeof Link>, 'color'>
  | (Omit<React.ComponentPropsWithoutRef<'button'>, 'color'> & {
      href?: undefined
    })
)

export function Button<Color extends ColorKey<Variant>, Variant extends VariantKey = 'primary'>({
  variant,
  color,
  className,
  target,
  rel,
  ...props
}: ButtonProps<Variant, Color>) {
  const validVariant = variant ?? ('primary' as Variant)
  const validColor = color ?? ('darkgreen' as Color)

  const combinedClassName = clsx(
    baseStyles[validVariant],
    variantStyles[validVariant][validColor] as ClassValue,
    className
  )

  return typeof props.href === 'undefined' ? (
    <button className={combinedClassName} {...props} />
  ) : (
    <Link className={combinedClassName} target={target} rel={rel} {...props} />
  )
}
